import React from 'react';
import CoachingReviewTypeStore, {typesAndCategories} from 'stores/coaching/CoachingReviewTypeStore';
import ScorecardStore from 'stores/ScorecardStore';
import ReviewTypeStep from 'components/coaching/wizard/steps/ReviewTypeStep';
import connectToStores from 'alt-utils/lib/connectToStores';
import ScorecardActions from 'actions/scorecards/ScorecardActions';
import {List, Set} from 'immutable';
import {validate} from 'records/CoachingPlan';
import pick from 'lodash/pick';
import {ensureReviewMetadata} from 'components/StatsPage2.utils';

class ReviewTypeStepContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.scorecardsBySkillIds = this.scorecardsBySkillIds.bind(this);
    this.state = {
      ready: false,
      skillFilteredScorecards: List(),
    };
  }

  static title = 'Review Meeting Agenda';

  static validate(plan) {
    const errors = validate(plan);
    return pick(errors, ['reviewType', 'scorecardId']);
  }

  /**
   * Determines whether this step should be rendered, using the props
   * of the wizard parent's props. Called during the `getSteps` function.
   *
   * @returns {boolean}
   */
  static shouldDisplay({sessionUser = {}, types = List()}) {
    return !types.isEmpty();
  }

  static getStores() {
    return [CoachingReviewTypeStore, ScorecardStore];
  }

  static getPropsFromStores() {
    const store = CoachingReviewTypeStore.getState();
    const {scorecards} = ScorecardStore.getState();

    const [, types] = typesAndCategories(CoachingReviewTypeStore);
    return {
      scorecards,
      types,
      defaultType: store.defaultType,
    };
  }

  componentDidMount() {
    const {onChange, model: coachingPlan, completedSteps, step, editMode, defaultType} = this.props;

    const isSkillBased = coachingPlan.contentDetails.some(detail => detail.isSkillBased);

    // keep this around so we only call onChange once
    let updatedPlan = coachingPlan;

    // If we're in edit mode don't overwrite review types.
    if (!editMode) {
      // if we already preloaded call review type, don't preload it again!
      if (!completedSteps.has(step)) {
        const ids = coachingPlan.reviewTypeIds.isEmpty()
          ? Set([defaultType.id])
          : coachingPlan.reviewTypeIds;

        updatedPlan = coachingPlan.merge({
          reviewTypeIds: ids,
          // must have skills for scorecard
          scorecardId: isSkillBased ? coachingPlan.scorecardId : null,
        });
      }

      ensureReviewMetadata(CoachingReviewTypeStore);
    }
    // don't need scorecards
    if (!isSkillBased) {
      onChange(updatedPlan.set('useGenericQuestions', true));
      return;
    }
    const skillIds = updatedPlan.contentDetails.map(d => d.skillId);
    const departmentId = coachingPlan.recipient.info.departmentId;
    ScorecardActions.fetchScorecardsForDepartment(departmentId, true, {
      repId: coachingPlan.recipient.info.userId,
      skillIds: skillIds.toArray(),
    }).then(cards => {
      if (cards && !cards.isEmpty()) {
        const skillFilteredScorecards = this.scorecardsBySkillIds(skillIds);
        if (!editMode) {
          if (skillFilteredScorecards.size > 0) {
            onChange(
              updatedPlan
                .set('useGenericQuestions', false)
                .set('scorecardId', skillFilteredScorecards.first().id)
            );
          } else {
            onChange(updatedPlan.set('useGenericQuestions', true));
          }
        }
        this.setState({
          ready: true,
          skillFilteredScorecards,
        });
      } else {
        if (!editMode) {
          onChange(updatedPlan.set('useGenericQuestions', true));
        }
      }
    });
  }

  scorecardsBySkillIds(skillIds) {
    const {scorecards} = this.props;
    return scorecards.filter(sc => sc.getQuestionSkillIds().intersect(skillIds).size > 0);
  }

  render() {
    const {skillFilteredScorecards: scorecards} = this.state;

    return (
      <ReviewTypeStep
        {...this.props}
        scorecards={scorecards}
        validate={ReviewTypeStepContainer.validate}
      />
    );
  }
}

const connectedComponent = connectToStores(ReviewTypeStepContainer);
connectedComponent.title = ReviewTypeStepContainer.title;
connectedComponent.validate = ReviewTypeStepContainer.validate;
connectedComponent.shouldDisplay = ReviewTypeStepContainer.shouldDisplay;
export default connectedComponent;
